import React, { useCallback, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'

import { formatDOB } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/userUtils'
import { ClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/Client'
import { FormInfoBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineInfoBanner/FormInfoBanner'
import { AddNewClientModal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/addNewClientModal/AddNewClientModal'

import { useAddNewClientModals } from './hooks/useAddNewClientModals'
import { mixpanelEvents } from '../../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../../mixpanel/mixpanelTracking'
import ClientsDashboard from '../../../../clients/dashboard/ClientsDashboard'
import { getAuthUserId } from '../../../../data/auth/authSelectors'

type LegacyProviderPortalTabProps = {
  onTransitionSuccess?: (isRegistered: boolean) => void
}

export const LegacyProviderPortalTab: React.FC<LegacyProviderPortalTabProps> = ({ onTransitionSuccess }) => {
  const { formatMessage } = useIntl()
  const userId = useSelector(getAuthUserId)
  const [transitionClient, setTransitionClient] = useState<ClientObject | null>(null)

  const {
    allCustomers,
    showAddNewClientModal,
    selectedCompany,
    submissionError,
    isAddingNewClient,
    careTypeOptions,
    isFetchingPrograms,
    addNewClient,
    openTransitionModal,
    closeAddClientModal,
    setSelectedCompany,
    setAddedClientFirstName,
    setShowSuccessModal,
    setSubmissionError,
    setToastContent: dispatchToast,
  } = useAddNewClientModals(userId)

  const eligibleEmployerValues = useMemo(() => new Set(allCustomers.map((customer) => customer.value)), [allCustomers])

  const isRegistered = !!transitionClient?.is_registered

  const handleAddToEngage = useCallback(
    (client: ClientObject) => {
      track({
        event: mixpanelEvents.LEGACY_CLIENT_ADD_TO_ENGAGE_CLICK,
        properties: { is_registered: !!client.is_registered },
      })
      setTransitionClient(client)
      openTransitionModal(eligibleEmployerValues.has(client.employer) ? client.employer : null)
    },
    [eligibleEmployerValues, openTransitionModal],
  )

  const initialValues = useMemo(() => {
    if (!transitionClient) return undefined
    //This is to guard a specific edge case for unregistered clients who have an email on record from outcome submission
    const prefillEmail = transitionClient.is_registered ? undefined : transitionClient.email?.trim() || undefined
    return {
      company: eligibleEmployerValues.has(transitionClient.employer) ? transitionClient.employer : '',
      firstName: transitionClient.first_name,
      lastName: transitionClient.last_name,
      dateOfBirth: formatDOB(transitionClient.date_of_birth),
      ...(prefillEmail && { email: prefillEmail }),
    }
  }, [eligibleEmployerValues, transitionClient])

  const handleAddSuccess = useCallback(() => {
    if (!transitionClient) return
    const firstName = transitionClient.first_name
    dispatchToast({
      text: isRegistered
        ? formatMessage(
            {
              defaultMessage: '{firstName} has been added to Engage and removed from your legacy list.',
              description: 'Toast after transitioning a registered legacy client to the Engage platform',
            },
            { firstName },
          )
        : formatMessage(
            {
              defaultMessage:
                'Registration link sent. {firstName} will appear as pending in your Invited list, and will move to Engage once they register.',
              description: 'Toast after sending a registration invite to an unregistered legacy client',
            },
            { firstName },
          ),
      id: 'LegacyProviderPortalTab-transitionSuccess',
      toastType: 'success',
    })
    onTransitionSuccess?.(isRegistered)
  }, [dispatchToast, formatMessage, isRegistered, onTransitionSuccess, transitionClient])

  const topContent = transitionClient ? (
    <FormInfoBanner
      testId='LegacyProviderPortalTab-transitionBanner'
      title={
        isRegistered
          ? formatMessage(
              {
                defaultMessage: '{firstName} has a Lyra account. Add them to your Engage client list.',
                description:
                  'Title of the banner in the add client modal when transitioning a registered legacy client',
              },
              { firstName: transitionClient.first_name },
            )
          : formatMessage(
              {
                defaultMessage: '{firstName} needs a Lyra account to add them to Engage.',
                description:
                  'Title of the banner in the add client modal when transitioning an unregistered legacy client',
              },
              { firstName: transitionClient.first_name },
            )
      }
      text={
        isRegistered ? (
          <FormattedMessage
            defaultMessage='Once added, you can schedule and invoice for any sessions from the calendar and view them on your Engage client list.'
            description='Banner body in the add client modal when transitioning a registered legacy client'
          />
        ) : (
          <FormattedMessage
            defaultMessage='Use this form to send {firstName} a registration invite. Once registered, you can schedule and invoice for any sessions from the calendar and view them on your Engage client list.'
            description='Banner body in the add client modal when transitioning an unregistered legacy client'
            values={{ firstName: transitionClient.first_name }}
          />
        )
      }
    />
  ) : undefined

  return (
    <>
      <ClientsDashboard onAddToEngage={handleAddToEngage} eligibleEmployerValues={eligibleEmployerValues} />
      <AddNewClientModal
        customers={allCustomers}
        providerId={userId}
        visible={showAddNewClientModal}
        onAddNewClient={addNewClient}
        isSubmitting={isAddingNewClient}
        onRequestClose={closeAddClientModal}
        track={track}
        careTypeOptions={careTypeOptions}
        isFetchingCareTypes={isFetchingPrograms}
        onCompanyChange={setSelectedCompany}
        selectedCompany={selectedCompany}
        setAddedClientFirstName={setAddedClientFirstName}
        setShowSuccessModal={setShowSuccessModal}
        setToastContent={dispatchToast}
        submissionError={submissionError}
        setSubmissionError={setSubmissionError}
        initialValues={initialValues}
        showEmailField={!isRegistered}
        topContent={topContent}
        transitionContext={transitionClient ? { lyraId: transitionClient.id, isRegistered } : undefined}
        onAddSuccess={handleAddSuccess}
        trackingProperties={{
          legacy_transition: true,
          is_registered: isRegistered,
          ...(transitionClient?.id && { client_lyra_id: transitionClient.id }),
        }}
      />
    </>
  )
}
